import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import MDEditor from '@uiw/react-md-editor';
import StateCard from '../../components/StateCard';
import { groupCarriersByName } from '../../utils/carrierHelpers';
import './AgentProfile.css';

function AgentProfile({ token }) {
  const navigate = useNavigate();
  const selectedCampaign = localStorage.getItem('selectedCampaign') || 'medicare';
  const isAcaCampaign = selectedCampaign.toLowerCase().includes('aca') || selectedCampaign.toLowerCase().includes('obamacare') || selectedCampaign.toLowerCase().includes('marketplace');
  const isMedicareCampaign = !isAcaCampaign;

  const [profile, setProfile] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedStates, setExpandedStates] = useState({});
  const [twoFactorStatus, setTwoFactorStatus] = useState({ enabled: false, configured: false });
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disabling, setDisabling] = useState(false);

  // ── Edit contact info ──
  const [editingContact, setEditingContact] = useState(false);
  const [editPhone, setEditPhone] = useState('');
  const [editInternalPhone, setEditInternalPhone] = useState('');
  const [savingContact, setSavingContact] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchCarriers();
    fetch2FAStatus();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get('/api/agent/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setLoading(false);
    }
  };

  const fetchCarriers = async () => {
    try {
      const response = await axios.get('/api/agent/carriers', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCarriers(response.data || []);
    } catch (error) {
      console.error('Error fetching carriers:', error);
    }
  };

  const fetch2FAStatus = async () => {
    try {
      const response = await axios.get('/api/2fa/status');
      setTwoFactorStatus(response.data);
    } catch (error) {
      console.error('Error fetching 2FA status:', error);
    }
  };

  const handleEnable2FA = () => {
    navigate('/agent/setup-2fa');
  };

  const handleReset2FA = () => {
    // Reset means disable and re-enable
    navigate('/agent/setup-2fa');
  };

  const handleDisable2FA = async () => {
    if (!disablePassword.trim()) {
      toast.error('Please enter your password');
      return;
    }

    setDisabling(true);
    try {
      const response = await axios.post('/api/2fa/disable', {
        password: disablePassword
      });

      if (response.data.success) {
        toast.success('Two-factor authentication disabled successfully');
        setShowDisableModal(false);
        setDisablePassword('');
        fetch2FAStatus(); // Refresh status
      }
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      toast.error(error.response?.data?.error || 'Failed to disable 2FA');
    } finally {
      setDisabling(false);
    }
  };

  const startEditContact = () => {
    setEditPhone(profile.phone || '');
    setEditInternalPhone(profile.internalPhone || '');
    setEditingContact(true);
  };

  const saveContact = async () => {
    setSavingContact(true);
    try {
      await axios.put('/api/agent/profile', {
        phone: editPhone.trim(),
        internalPhone: editInternalPhone.trim()
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Contact info updated successfully!');
      setEditingContact(false);
      fetchProfile(); // refresh
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save changes');
    } finally {
      setSavingContact(false);
    }
  };

  const toggleState = (memberEmail, state) => {
    const key = `${memberEmail}-${state}`;
    setExpandedStates(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (loading) return <div className="loading">Loading profile...</div>;
  if (!profile) return <div className="error">Error loading profile</div>;

  // Check if this is a group member login
  const isMemberLogin = profile._isMemberLogin;
  const loggedInMemberEmail = profile._loggedInMemberEmail;

  // Check if this is a group with members
  const isGroup = profile.agentType === 'group' && profile.groupMembers && profile.groupMembers.length > 0;

  if (isGroup) {
    // For member logins: sort so logged-in member appears first
    const sortedMembers = isMemberLogin && loggedInMemberEmail
      ? [
          ...profile.groupMembers.filter(m => m.email === loggedInMemberEmail),
          ...profile.groupMembers.filter(m => m.email !== loggedInMemberEmail),
        ]
      : profile.groupMembers;

    // Show all group members as separate cards
    return (
      <div className="container">
        <h2>Group Profile: {profile.ghlLocationName || profile.name}</h2>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>
          Showing all {profile.groupMembers.length} member{profile.groupMembers.length !== 1 ? 's' : ''} in this group
        </p>
        
        {sortedMembers.map((member, index) => {
          const isLoggedInMember = isMemberLogin && member.email === loggedInMemberEmail;
          return (
          <div key={member.email} className="profile-view" style={{ marginBottom: '32px', border: isLoggedInMember ? '2px solid #059669' : '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', position: 'relative' }}>
            {isLoggedInMember && (
              <div style={{ position: 'absolute', top: '12px', right: '12px', background: '#d1fae5', color: '#059669', fontSize: '11px', fontWeight: 700, borderRadius: '999px', padding: '3px 10px' }}>
                ✓ You
              </div>
            )}
            <div className="profile-header">
              <h3>{member.name || member.email}</h3>
              <p>{member.email}</p>
              {member.npn && <p className="npn">NPN: {member.npn}</p>}
              {member.healthSherpaMarketingLink && (
                <p style={{ marginTop: '8px' }}>
                  <strong>HealthSherpa Marketing Link:</strong>{' '}
                  <a href={member.healthSherpaMarketingLink} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                    {member.healthSherpaMarketingLink}
                  </a>
                </p>
              )}
            </div>

            {member.statesWithCarriers && Object.keys(member.statesWithCarriers).length > 0 && (
              <div className="profile-section" style={{ marginTop: '24px' }}>
                <h4>States & Carriers</h4>
                <div className="states-list">
                  {Object.entries(member.statesWithCarriers)
                    .filter(([state]) => !member.activeStates || member.activeStates.includes(state))
                    .map(([state, stateCarriers]) => (
                      <StateCard
                        key={state}
                        stateCode={state}
                        carriers={stateCarriers}
                        allCarriers={carriers}
                        onUpdate={() => {}}
                        onDelete={() => {}}
                        isEditing={false}
                        setIsEditing={() => {}}
                        isAdminView={false}
                        readOnly={true}
                      />
                    ))}
                </div>
              </div>
            )}

            {member.consent && (
              <div className="profile-section" style={{ marginTop: '24px' }}>
                <h4>Consent</h4>
                <MDEditor.Markdown source={member.consent} />
              </div>
            )}
            {isAcaCampaign && member.nonFfmConsent && (
              <div className="profile-section" style={{ marginTop: '24px' }}>
                <h4>Non-FFM Consent</h4>
                <MDEditor.Markdown source={member.nonFfmConsent} />
              </div>
            )}
            {isMedicareCampaign && member.soaConsent && (
              <div className="profile-section" style={{ marginTop: '24px' }}>
                <h4>SOA Consent</h4>
                <MDEditor.Markdown source={member.soaConsent} />
              </div>
            )}
          </div>
          );
        })}
      </div>
    );
  }

  // Regular agent (non-group) - show single profile
  return (
    <div className="container">
      <h2>My Profile</h2>
      
      <div className="profile-view">
        {/* 2FA Security Section */}
        <div className={`profile-2fa-banner ${!twoFactorStatus.enabled ? 'not-enabled' : ''}`}>
          <div className="profile-2fa-header">
            <div className="profile-2fa-info">
              <h4>
                <span style={{ fontSize: '20px' }}>🛡️</span>
                Two-Factor Authentication
              </h4>
              <p>
                {twoFactorStatus.enabled ? (
                  <>✅ <strong>Enabled</strong> - Your account is secured with 2FA</>
                ) : (
                  <>⚠️ <strong>Not Enabled</strong> - Protect your account by enabling 2FA</>
                )}
              </p>
            </div>
            
            <div className="profile-2fa-actions">
              {!twoFactorStatus.enabled ? (
                <button onClick={handleEnable2FA}>
                  🔒 Enable 2FA
                </button>
              ) : (
                <>
                  <button onClick={handleReset2FA} className="reset-btn">
                    🔄 Reset 2FA
                  </button>
                  <button onClick={() => setShowDisableModal(true)} className="disable-btn">
                    🚫 Disable 2FA
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Disable 2FA Modal */}
        {showDisableModal && (
          <div className="disable-2fa-modal-overlay">
            <div className="disable-2fa-modal-content">
              <h3>
                ⚠️ Disable Two-Factor Authentication
              </h3>
              <p>
                Are you sure you want to disable 2FA? Your account will be less secure. Please enter your password to confirm.
              </p>
              
              <div style={{ marginBottom: '24px' }}>
                <label>Password</label>
                <input
                  type="password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  placeholder="Enter your password"
                  onKeyPress={(e) => e.key === 'Enter' && handleDisable2FA()}
                />
              </div>
              
              <div className="disable-2fa-modal-actions">
                <button
                  onClick={() => {
                    setShowDisableModal(false);
                    setDisablePassword('');
                  }}
                  disabled={disabling}
                  className="cancel-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDisable2FA}
                  disabled={disabling}
                  className="confirm-btn"
                  style={{ opacity: disabling ? 0.6 : 1 }}
                >
                  {disabling ? 'Disabling...' : 'Yes, Disable 2FA'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="profile-header" style={{ marginTop: '24px' }}>
          <h3>{profile.name}</h3>
            <p>{profile.email}</p>
          <p className="npn">NPN: {profile.npn}</p>
          {/* ── Contact Info Card ── */}
          <div style={{ marginTop: 16, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px', textAlign: 'left', maxWidth: 480, marginLeft: 'auto', marginRight: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: editingContact ? 12 : 6 }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: '#374151' }}>📞 Contact Info</span>
              {!editingContact && (
                <button onClick={startEditContact} style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '3px 10px', fontSize: 12, color: '#374151', cursor: 'pointer' }}>✏️ Edit</button>
              )}
            </div>

            {editingContact ? (
              <div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Business Phone</label>
                  <input
                    type="tel"
                    value={editPhone}
                    onChange={e => setEditPhone(e.target.value)}
                    placeholder="+1 (555) 000-0000"
                    style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                  />
                  <div style={{ marginTop: 5, padding: '6px 8px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 5, fontSize: 11, color: '#0369a1', lineHeight: 1.5 }}>
                    📱 <strong>Business phone</strong> — shown to your clients in consent forms and conversations.
                  </div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Personal Phone <span style={{ fontWeight: 400, color: '#6b7280' }}>(Optional)</span></label>
                  <input
                    type="tel"
                    value={editInternalPhone}
                    onChange={e => setEditInternalPhone(e.target.value)}
                    placeholder="+1 (555) 000-0000"
                    style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                  />
                  <div style={{ marginTop: 5, padding: '6px 8px', background: '#fefce8', border: '1px solid #fde047', borderRadius: 5, fontSize: 11, color: '#854d0e', lineHeight: 1.5 }}>
                    🔒 <strong>Personal phone</strong> — used for login OTP and password reset only. Never shown to clients.
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={saveContact} disabled={savingContact} style={{ flex: 1, padding: '8px', background: '#1a56db', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: savingContact ? 'not-allowed' : 'pointer', opacity: savingContact ? 0.7 : 1 }}>
                    {savingContact ? 'Saving…' : '✓ Save Changes'}
                  </button>
                  <button onClick={() => setEditingContact(false)} style={{ padding: '8px 14px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: '#4b5563' }}>
                <div style={{ marginBottom: 4 }}>
                  <span style={{ color: '#6b7280' }}>📱 Business Phone:</span>{' '}
                  <strong>{profile.phone || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Not set</span>}</strong>
                  <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 6 }}>(shown to clients &amp; in consent)</span>
                </div>
                <div>
                  <span style={{ color: '#6b7280' }}>🔒 Personal Phone:</span>{' '}
                  <strong>{profile.internalPhone || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Not set</span>}</strong>
                  {profile.internalPhone
                    ? <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 6 }}>(for login &amp; OTP only)</span>
                    : <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 6 }}>(optional — for login &amp; OTP only)</span>
                  }
                </div>
              </div>
            )}
          </div>
          
          {/* HealthSherpa Marketing Link - Read-only display */}
          {profile.healthSherpaMarketingLink && (
            <div className="healthsherpa-link-box">
              <strong>
                🔗 HealthSherpa Marketing Link
              </strong>
              <a 
                href={profile.healthSherpaMarketingLink} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                {profile.healthSherpaMarketingLink}
              </a>
              <div className="hint-text">
                💡 To update this link, use "Request Changes" page
              </div>
            </div>
          )}
        </div>

        <div className="profile-section" style={{ marginTop: '32px' }}>
          <h4>States & Carriers</h4>
          <div className="states-list">
            {Object.entries(profile.statesWithCarriers || {})
              .filter(([state]) => !profile.activeStates || profile.activeStates.includes(state))
              .map(([state, stateCarriers]) => (
                <StateCard
                  key={state}
                  stateCode={state}
                  carriers={stateCarriers}
                  allCarriers={carriers}
                  onUpdate={() => {}}
                  onDelete={() => {}}
                  isEditing={false}
                  setIsEditing={() => {}}
                  isAdminView={false}
                  readOnly={true}
                />
              ))}
          </div>
        </div>

        <div className="profile-section consent-section" style={{ marginTop: '32px' }}>
          <h4>Consent</h4>
          <MDEditor.Markdown source={profile.consent} />
        </div>
        {isAcaCampaign && profile.nonFfmConsent && (
          <div className="profile-section consent-section" style={{ marginTop: '32px' }}>
            <h4>Non-FFM Consent</h4>
            <MDEditor.Markdown source={profile.nonFfmConsent} />
          </div>
        )}
        {isMedicareCampaign && profile.soaConsent && (
          <div className="profile-section consent-section" style={{ marginTop: '32px' }}>
            <h4>SOA Consent</h4>
            <MDEditor.Markdown source={profile.soaConsent} />
          </div>
        )}
      </div>
    </div>
  );
}

export default AgentProfile;
